import liff from "@line/liff";
import { useEffect, useState, useMemo } from "react";
import type { ReactNode } from "react";
import { LiffContext } from "./LineContext";
import type { LiffContextType } from "./LineContext";

interface LiffProviderProps {
  children: ReactNode;
}

/**
 * LIFF Provider Component
 *
 * Initializes LINE LIFF SDK and provides authentication context to all child components.
 * Uses React 19 direct context rendering pattern for improved performance.
 *
 * **React 19 Context Pattern:**
 * - Returns `<LiffContext value={...}>` instead of `<LiffContext.Provider value={...}>`
 * - Context value is memoized with useMemo to prevent unnecessary consumer re-renders
 * - Only state variables in dependency array (methods are stable, excluded per React 19 best practices)
 *
 * **State Management:**
 * - `isLiffReady`: LIFF SDK initialization complete
 * - `isLoggedIn`: User has authenticated with LINE
 * - `userId`: LINE user ID (from LIFF context)
 * - `idToken`: LINE ID token (JWT, contains user claims)
 * - `accessToken`: LINE access token (12hr validity)
 * - `error`: LIFF initialization error (if any)
 *
 * **Methods:**
 * - `login()`: Trigger LINE login flow
 * - `logout()`: LINE logout and clear local state
 * - `closeWindow()`: Close LIFF window (in-app browser only)
 *
 * @param children - Child components that will have access to LIFF context
 *
 * @example
 * ```tsx
 * <LiffProvider>
 *   <App />
 * </LiffProvider>
 * ```
 */
export function LiffProvider({ children }: LiffProviderProps) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLiffReady, setIsLiffReady] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initializeLiff = async () => {
      try {
        const liffId = import.meta.env.VITE_LIFF_ID;

        if (!liffId) {
          throw new Error("LIFF ID is not configured. Please set VITE_LIFF_ID");
        }

        await liff.init({ liffId, withLoginOnExternalBrowser: true });
        setIsLiffReady(true);

        if (liff.isLoggedIn()) {
          setIsLoggedIn(true);

          // Get userId from context
          const contextUserId = liff.getContext()?.userId;
          if (contextUserId) {
            setUserId(contextUserId);
          }

          // Get ID token (JWT)
          const token = liff.getIDToken();
          if (token) {
            setIdToken(token);
          }

          // Get access token (valid for 12 hours)
          const accToken = liff.getAccessToken();
          if (accToken) {
            setAccessToken(accToken);
          }
        } else {
          // User not logged into LINE - automatically trigger LINE login
          // This makes LINE login a transparent prerequisite for server auth
          liff.login();
        }
      } catch (err) {
        console.error("LIFF initialization failed:", err);
        setError(
          err instanceof Error ? err : new Error("LIFF initialization failed"),
        );
      }
    };

    initializeLiff();
  }, []);

  const login = () => {
    if (!liff.isLoggedIn()) {
      liff.login();
    }
  };

  const logout = () => {
    if (liff.isLoggedIn()) {
      liff.logout();
      setIsLoggedIn(false);
      setUserId(null);
      setIdToken(null);
      setAccessToken(null);
    }
  };

  const closeWindow = () => {
    liff.closeWindow();
  };

  /**
   * Memoize context value to prevent unnecessary re-renders of consumers
   *
   * **Why useMemo?**
   * Without memoization, the context value object would be recreated on every render,
   * causing ALL consumers to re-render even when state hasn't changed.
   *
   * **Dependency Array Strategy (React 19 Best Practice):**
   * - Include: State variables (isLoggedIn, isLiffReady, userId, idToken, accessToken, error)
   * - Exclude: Methods (login, logout, closeWindow) - these are stable function references
   *
   * Methods don't need to be in dependencies because:
   * 1. They don't close over any changing values
   * 2. React 19 Compiler handles stable function optimization automatically
   * 3. Including them would cause unnecessary re-memoization on every render
   *
   * This pattern ensures consumers ONLY re-render when actual state changes.
   */
  const value = useMemo<LiffContextType>(
    () => ({
      isLoggedIn,
      isLiffReady,
      userId,
      idToken,
      accessToken,
      error,
      login,
      logout,
      closeWindow,
    }),
    [isLoggedIn, isLiffReady, userId, idToken, accessToken, error],
  );

  // React 19: Use direct context rendering instead of Context.Provider
  return <LiffContext value={value}>{children}</LiffContext>;
}
